'use client';

import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react';
import type { RainaExpression } from './useAnimationState';

/** Click regions for interactive mode */
type ClickRegion = 'ear' | 'head' | 'body' | 'hand';

/** Reaction: expression + animation pairing for click interactions */
interface ClickReaction {
  expression: RainaExpression;
  duration: number;
  animation: 'nod' | 'earTwitch' | 'wave' | 'headTilt';
}

const EAR_REACTIONS: ClickReaction[] = [
  { expression: 'surprised', duration: 400, animation: 'earTwitch' },
  { expression: 'curious', duration: 500, animation: 'earTwitch' },
  { expression: 'confused', duration: 500, animation: 'earTwitch' },
  { expression: 'impressed', duration: 500, animation: 'earTwitch' },
];

const HEAD_REACTIONS: ClickReaction[] = [
  { expression: 'happy', duration: 800, animation: 'nod' },
  { expression: 'excited', duration: 800, animation: 'nod' },
  { expression: 'proud', duration: 800, animation: 'nod' },
  { expression: 'curious', duration: 1000, animation: 'headTilt' },
  { expression: 'impressed', duration: 800, animation: 'nod' },
  { expression: 'celebrating', duration: 800, animation: 'nod' },
];

const HAND_REACTIONS: ClickReaction[] = [
  { expression: 'happy', duration: 1300, animation: 'wave' },
  { expression: 'excited', duration: 1300, animation: 'wave' },
  { expression: 'celebrating', duration: 1300, animation: 'wave' },
  { expression: 'proud', duration: 1300, animation: 'wave' },
];

const pickRandom = <T>(arr: readonly T[]): T => {
  if (arr.length === 0) {
    throw new Error('pickRandom: array must not be empty');
  }
  return arr[Math.floor(Math.random() * arr.length)] as T;
};

export interface UseRainaInteractionOptions {
  /** Enable click interactions */
  interactive: boolean;
  /** Custom click handler (overrides default reaction) */
  onClick?: () => void;
  /** Whether any animation is currently active */
  isAnimating: boolean;
  /** Display variant: 'full' or 'head' */
  isHeadOnly: boolean;
}

export interface UseRainaInteractionResult {
  /** Temporary expression from interaction */
  interactionExpression: RainaExpression | null;
  /** Pending animation to be triggered by parent */
  pendingAnimation: 'nod' | 'earTwitch' | 'wave' | 'headTilt' | null;
  /** Click handler */
  handleClick: (e: MouseEvent<HTMLDivElement>) => void;
  /** Keyboard handler */
  handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Hook for managing Raina's click and keyboard interactions.
 *
 * Handles region-based reactions with temporary expressions and animations.
 */
export function useRainaInteraction({
  interactive,
  onClick,
  isAnimating,
  isHeadOnly,
}: UseRainaInteractionOptions): UseRainaInteractionResult {
  const [interactionExpression, setInteractionExpression] = useState<RainaExpression | null>(null);
  const [pendingAnimation, setPendingAnimation] = useState<'nod' | 'earTwitch' | 'wave' | 'headTilt' | null>(null);
  const interactionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setTemporaryExpression = (expr: RainaExpression, durationMs: number) => {
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    setInteractionExpression(expr);
    interactionTimeoutRef.current = setTimeout(() => setInteractionExpression(null), durationMs);
  };

  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    };
  }, []);

  // Use elementsFromPoint for accurate region detection through overlapping SVG layers
  const getRegionFromPoint = (e: MouseEvent<HTMLDivElement>) => {
    const elements = document.elementsFromPoint(e.clientX, e.clientY);
    for (const element of elements) {
      if (!(element instanceof Element)) continue;
      const region = element.getAttribute('data-region') as ClickRegion | null;
      if (region) return region;
    }
    return null;
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const region = getRegionFromPoint(e);
    if (!region) return;

    if (onClick) {
      onClick();
      return;
    }

    if (!interactive || isAnimating) return;

    const reaction = (() => {
      switch (region) {
        case 'ear':
          return pickRandom(EAR_REACTIONS);
        case 'hand':
          return isHeadOnly ? pickRandom(HEAD_REACTIONS) : pickRandom(HAND_REACTIONS);
        case 'head':
        case 'body':
        default:
          return pickRandom(HEAD_REACTIONS);
      }
    })();

    setTemporaryExpression(reaction.expression, reaction.duration);
    setPendingAnimation(reaction.animation);
  };

  // Keyboard activation without point-based region detection
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      if (onClick) {
        onClick();
        return;
      }
      if (!interactive || isAnimating) return;
      setTemporaryExpression('happy', 800);
      setPendingAnimation('nod');
    }
  };

  return {
    interactionExpression,
    pendingAnimation,
    handleClick,
    handleKeyDown,
  };
}
